import { useEffect, useRef, useState } from "react";
import {
  IonContent,
  IonItem,
  IonList,
  IonListHeader,
  IonModal,
  IonRadio,
  IonRadioGroup,
} from "@ionic/react";
import { PositionModel } from "model";
import { Badge, Colors, useFeatureFlags } from "swing-components";

import { translate } from "~utils";

import { ModalHeader } from "../ModalHeader";
import { SelectItem } from "../SelectItem";
import { filterPositionsByName, groupPositionsByRateType, type PositionRateGroup } from "./utils";

type PositionFieldProps = {
  /** The options to select */
  positions: PositionModel[];
  /** The selected option */
  selectedPosition?: PositionModel;
  onChange: (item?: PositionModel) => void;
  isDisabled?: boolean;
};

type GroupedPositions = ReturnType<typeof groupPositionsByRateType>;

export function PositionField(props: PositionFieldProps) {
  /*** Props ***/
  const { positions, selectedPosition, onChange, isDisabled } = props;
  const { flags } = useFeatureFlags();
  const isPositionAdjustmentEnabled = Boolean(flags?.positionAdjustment);

  /*** Refs ***/
  const positionModal = useRef<HTMLIonModalElement>(null);

  /*** State ***/
  const [temporarySelectedPosition, setTemporarySelectedPosition] = useState(selectedPosition);
  const [searchTerm, setSearchTerm] = useState("");

  /*** Constants ***/
  const groupedPositions = groupPositionsByRateType(filterPositionsByName(positions, searchTerm));

  /** Effects */
  /** Reset the temporary state when the `selectedPositions` prop changes */
  useEffect(() => {
    setTemporarySelectedPosition(selectedPosition);
  }, [selectedPosition]);

  /*** Handlers ***/
  /**
   * When dismissing the Modal, reset the temporary state and do not pass along
   * the change to the parent
   */
  const handleModalDismiss = () => {
    setTemporarySelectedPosition(selectedPosition);
    setSearchTerm("");
    positionModal.current?.dismiss();
  };

  /** When confirming the Modal, pass long the changes to the parent */
  const handleModalConfirm = () => {
    onChange(temporarySelectedPosition);
    setSearchTerm("");
    positionModal.current?.dismiss();
  };

  /*** Render ***/
  return (
    <SelectItem
      id="position-select-modal"
      label={translate("LABEL_POSITION_FIELD")}
      // Using `selectedPosition` since we do not want to change this name until
      // it's no longer a temporary change.
      value={selectedPosition?.displayName}
      isDisabled={isDisabled}
      stacked
      css={isDisabled ? { color: Colors.slate300, cursor: "not-allowed" } : undefined}
      isRequired={isPositionAdjustmentEnabled}
    >
      <IonModal
        aria-label="Position Selection"
        trigger="position-select-modal"
        ref={positionModal}
        // TODO: Should we have this action not save the users choice?
        // Handles when clicking on the background
        onDidDismiss={handleModalDismiss}
        className="modal-600 stackedModal"
      >
        <ModalHeader
          title={translate("LABEL_POSITION_FIELD")}
          // Handles when clicking on "Cancel"
          onModalDismiss={handleModalDismiss}
          // Handles when clicking on "Done"
          onModalConfirm={handleModalConfirm}
          onIonInput={(e) => setSearchTerm(e?.target?.value || "")}
        />
        <IonContent>
          <PositionList
            groupedPositions={groupedPositions}
            selectedPosition={temporarySelectedPosition}
            onPositionSelect={setTemporarySelectedPosition}
          />
          <p
            css={{
              color: Colors.black,
              fontSize: 14,
              lineHeight: "20px",
              margin: 8,
              padding: "8px 8px 16px",
            }}
          >
            {translate("POSITION_CUSTOM_POSITION_MESSAGE")}
          </p>
        </IonContent>
      </IonModal>
    </SelectItem>
  );
}

function PositionList(props: {
  groupedPositions: GroupedPositions;
  selectedPosition?: PositionModel;
  onPositionSelect: (position: PositionModel) => void;
}) {
  const { groupedPositions, selectedPosition, onPositionSelect } = props;

  return (
    <IonList css={{ background: "transparent", padding: 0 }}>
      <IonRadioGroup
        value={selectedPosition?.id}
        css={{
          display: "flow-root",
        }}
      >
        {groupedPositions.map(({ group, positions }) => (
          <PositionGroup
            key={group}
            group={group}
            positions={positions}
            onPositionSelect={onPositionSelect}
          />
        ))}
      </IonRadioGroup>
    </IonList>
  );
}

function PositionGroup(props: {
  group: PositionRateGroup;
  positions: PositionModel[];
  onPositionSelect: (position: PositionModel) => void;
}) {
  const { group, positions, onPositionSelect } = props;

  return (
    <div
      css={{
        backgroundColor: Colors.white200,
        borderRadius: 8,
        margin: 8,
        overflow: "hidden",
      }}
    >
      <IonListHeader
        css={{
          "--background": Colors.teal200,
          color: Colors.black,
          display: "flex",
          borderRadius: 8,
          fontSize: 16,
          fontWeight: 600,
          minHeight: 36,
          padding: 16,
        }}
      >
        {getPositionGroupLabel(group)}
      </IonListHeader>
      {positions.map((position) => (
        <PositionOption key={position.id} position={position} onPositionSelect={onPositionSelect} />
      ))}
    </div>
  );
}

function PositionOption(props: {
  position: PositionModel;
  onPositionSelect: (position: PositionModel) => void;
}) {
  const { position, onPositionSelect } = props;

  return (
    <IonItem onClick={() => onPositionSelect(position)}>
      <IonRadio labelPlacement="end" justify="start" value={position.id} css={{ width: "100%" }}>
        <div css={{ display: "flex", flexDirection: "column", gap: 4, padding: "10px 0" }}>
          <span>{position.displayName}</span>
          {position.credsRequired && (
            <Badge status="success" text={translate("POSITION_CREDENTIAL_REQUIRED_TAG")} />
          )}
        </div>
      </IonRadio>
    </IonItem>
  );
}

function getPositionGroupLabel(group: PositionRateGroup) {
  switch (group) {
    case "daily":
      return translate("POSITION_RATE_GROUP_DAILY");
    case "hourly":
      return translate("POSITION_RATE_GROUP_HOURLY");
    case "others":
      return translate("POSITION_RATE_GROUP_OTHERS");
  }
}
